import { ColorPaletteSetting } from "types";
import { buildColormapPalette } from "utils/color/buildColormapPalette";
import { hexToRgb } from "utils/color/rgbHelpers";

import { PaletteType } from "models/colorPalette";

/**
 * "Jet" system palette (mirrors the `COLOR_PALETTES` entry in
 * constants/settings.constants) expressed as a full ColorPaletteSetting so the
 * backend StyleSchemaFactory colours the rig/frac bars from it. No longer the
 * rig/frac default (see RIG_FRAC_PLASMA_PALETTE below), but it stays exported
 * and selectable, and is what a generated palette falls back to if the
 * colormap generator ever fails to build Plasma.
 *
 * `colorScale` evenly spaces the four Jet stops (0 / 0.33 / 0.67 / 1) — the same
 * 4-stop pattern the app uses for its other default palettes.
 */
export const JET_COLOR_PALETTE: ColorPaletteSetting = {
  id: "jet",
  name: "Jet",
  colors: ["rgb(0,0,127)", "rgb(0, 160, 255)", "rgb(234, 243, 13)", "rgb(159, 7, 0)"],
  colorScale: [
    { index: 0, rgbColor: "rgb(0,0,127)" },
    { index: 0.33, rgbColor: "rgb(0, 160, 255)" },
    { index: 0.67, rgbColor: "rgb(234, 243, 13)" },
    { index: 1, rgbColor: "rgb(159, 7, 0)" }
  ],
  reverse: false,
  shared: false,
  paletteType: PaletteType.Continuous,
  thickness: [],
  preferredColors: [],
  specialColors: null
};

export interface GeneratedPaletteIdentity {
  id: string;
  name: string;
  // Hex shades — what buildColormapPalette (and, by extension, the
  // ColorPaletteList palette picker's generated entries) emit.
  colors: string[];
  // Set by the picker's reverse button, which toggles this flag and leaves
  // `colors` in their original order. Carried through untouched: both the
  // server's own colour handling and generateColors (the client-side path)
  // read this flag, so reversing the array here as well would double up.
  reverse?: boolean;
}

/**
 * Converts a generated colormap palette (hex colours, no colorScale) into the
 * `rgb()` + evenly-spaced-colorScale shape chart-svc's ParseRgbColor /
 * ConvertRgbStringToByteArray can read — a hex value comes back opaque black
 * at best, and throws at worst. `id` and `name` pass through unchanged:
 * useRigFracSeriesColors' default-palette discriminator keys on `id`, and
 * ColorPaletteList highlights the selected entry by `name`, so neither may
 * drift in translation. Shared by every rig/frac caller that can hand a
 * generated palette to the chart request — do not re-derive this
 * hex/colorScale conversion at the call site.
 *
 * `reverse` passes through as the picker set it, with `colors` left in their
 * original order, which is exactly the shape an org palette arrives in. Two
 * things depend on that. The chart request memo keys the palette on its id and
 * its reverse flag, so a reversed pick has to differ in one of them or the
 * re-post never fires and the bars keep their old colours. And the picker's own
 * preview strip re-derives from the selected palette's reverse flag, so
 * flattening it here would leave the strip and the chart disagreeing.
 */
export function convertGeneratedPaletteToChartRequestShape(
  generated: GeneratedPaletteIdentity
): ColorPaletteSetting {
  const rgbColors = generated.colors.map((hex) => {
    const { r, g, b } = hexToRgb(hex.replace("#", ""));
    return `rgb(${r}, ${g}, ${b})`;
  });

  const colorScale = rgbColors.map((rgbColor, index) => ({
    index: rgbColors.length > 1 ? index / (rgbColors.length - 1) : 0,
    rgbColor
  }));

  return {
    id: generated.id,
    name: generated.name,
    colors: rgbColors,
    colorScale,
    reverse: generated.reverse === true,
    shared: false,
    paletteType: PaletteType.Continuous,
    thickness: [],
    preferredColors: [],
    specialColors: null
  };
}

/**
 * Builds the rig/frac Plasma default in the `rgb()` shape chart-svc can parse,
 * via the shared conversion above. `buildColormapPalette("plasma")` (the same
 * generator the map's Plasma default and the palette list use) emits hex with
 * no `colorScale`, hence the conversion.
 */
function buildPlasmaColorPalette(): ColorPaletteSetting {
  const generated = buildColormapPalette("plasma");
  if (!generated) {
    return JET_COLOR_PALETTE;
  }

  // Same id/name every time (not the generator's own id) so the picker
  // highlights Plasma regardless of how buildColormapPalette names its
  // output, and so useRigFracSeriesColors' palette-id discriminator has a
  // stable target.
  return convertGeneratedPaletteToChartRequestShape({
    id: "generated-plasma",
    name: "Plasma",
    colors: generated.colors
  });
}

/**
 * The shared rig/frac default palette — Plasma, expressed in `rgb()` form so
 * chart-svc can render it when the chart isn't client-coloured (a non-Operator
 * group-by). Same generator the map's Plasma default uses, so an operator
 * resolves to the identical colour on the map and in the chart when neither
 * focus colours nor a user override apply.
 */
export const RIG_FRAC_PLASMA_PALETTE: ColorPaletteSetting = buildPlasmaColorPalette();
